import { useQuery } from '@tanstack/react-query';

import {
  transactionsService,
  payCalendarsService,
  FetchTransactionsRequest,
  individualsService,
  companiesService,
  FetchPayCalendarsResponse,
  FetchIndividualsResponse,
  FetchCompaniesResponse,
  useToast,
} from '@aslanpay/portal-common-lib';

import { format, parseISO } from 'date-fns';

import { TransactionsPage } from 'app/components/UI/Table/Table.types';

const useFetchTransactionsPage = (
  {
    ids,
    companyIds,
    individualIds,
    walletIds,
    directions,
    categories,
    status,
    startDate,
    endDate,
    minAmount,
    maxAmount,
    countryCodes,
    riskRuleCodes,
    isSuspicious,
    query,
    sortBy,
    sort,
    page,
    size,
  }: FetchTransactionsRequest,
  enabled = true
) => {
  const toast = useToast();

  return useQuery<TransactionsPage, Error>(
    [
      'fetchTransactionsPage',
      ids,
      companyIds,
      individualIds,
      walletIds,
      directions,
      categories,
      status,
      startDate,
      endDate,
      minAmount,
      maxAmount,
      countryCodes,
      riskRuleCodes,
      isSuspicious,
      query,
      sortBy,
      sort,
      page,
      size,
    ],
    async () => {
      const transactions = await transactionsService.fetchTransactions({
        ids,
        companyIds,
        individualIds,
        walletIds,
        directions,
        categories,
        status,
        startDate,
        endDate,
        minAmount,
        maxAmount,
        countryCodes,
        riskRuleCodes,
        isSuspicious,
        query,
        sortBy,
        sort,
        page,
        size,
      });

      let payCalendars: FetchPayCalendarsResponse = {
        list: [],
        pageNumber: 0,
        pageSize: 0,
        total: 0,
      };

      let employees: FetchIndividualsResponse = {
        list: [],
        pageNumber: 0,
        pageSize: 0,
        total: 0,
      };

      let companies: FetchCompaniesResponse = {
        list: [],
        pageNumber: 0,
        pageSize: 0,
        total: 0,
      };

      if (!isSuspicious) {
        const payCalendarIds = Array.from(
          new Set(transactions.list.map((transaction) => transaction.payCalendarId))
        ).filter(Boolean);

        if (payCalendarIds.length) {
          payCalendars = await payCalendarsService.fetchPayCalendars({
            ids: payCalendarIds,
            companyId: companyIds,
            page: 0,
            size: 999999,
          });
        } else {
          payCalendars = {
            list: [],
            pageNumber: 0,
            pageSize: 0,
            total: 0,
          };
        }
      } else {
        employees = await individualsService.fetchIndividuals({
          ids: Array.from(
            new Set(transactions.list.map((transaction) => transaction.individualId))
          ),
          role: ['EMPLOYEE'],
          page: 0,
          size: 999999,
        });

        companies = await companiesService.fetchCompanies({
          ids: Array.from(new Set(transactions.list.map((transaction) => transaction.companyId))),
          page: 0,
          size: 999999,
        });
      }

      const transactionsList = transactions.list.map((transaction) => {
        const payCycle = payCalendars.list
          .find((calendar) => calendar.id === transaction.payCalendarId)
          ?.payCycles.find((cycle) => cycle.id === transaction.payCycleId);

        const payCycleName = payCycle
          ? format(parseISO(payCycle.startDate as string), 'MMMM yyyy')
          : '';

        const companyName =
          companies.list.find((company) => company.id === transaction.companyId)?.name || '';

        const employee = employees.list.find((emp) => emp.id === transaction.individualId);
        const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : '';

        return { ...transaction, payCycleName, companyName, employeeName };
      });

      return { ...transactions, list: transactionsList };
    },
    {
      enabled,
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: `Sorry, we failed to fetch the transactions`,
        });
      },
    }
  );
};

export default useFetchTransactionsPage;
